// Overlay full-screen para el step `configure` cuando el preset es
// `catalogo-ia`. Muestra una card por cada (vista × producto)
// compatible — el user puede remover combos individuales antes de
// generar. Cada card replica el layout de los slots multi-modelo:
// imagen de la vista a la izquierda, nombre + thumb del producto a
// la derecha, X en la esquina para descartar la combinación.
//
// Filtrado de compatibilidad (helper `isVistaCompatibleConModelView`):
//   • con-modelo (poses) → se combina solo con productos cuyo `modelView`
//                          está en `areasPrincipales` de la pose (los
//                          tipos de producto sugeridos). Una pose que
//                          enfoca la cabeza no va con un zapato.
//   • solo-producto      → se combina solo con productos cuyo `modelView`
//                          matchea `compatibleModelViews` de la vista.

import React, { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Image as ImageIcon, Minus, Pencil, Plus, Ruler, Shirt, Sparkles, UserRound, X } from 'lucide-react';
import type { MockProductV2 } from '../mockData';
import type { PoseV2 } from '../posesApi';
import {
  isVistaCompatibleConModelView,
  resolveVistaImageUrl,
  type VistaCatalogo,
} from '../vistasCatalogo';
import { MultiSlotProductPickerSheet } from './MultiSlotProductPickerSheet';
import { scrollToAndShake } from '../scrollShake';
import { AgregarPoseSheet } from './AgregarPoseSheet';
import { AnimatedBlurBg } from '../AnimatedBlurBg';
import CachedImg from '../../../components/CachedImg';
import type { FondoSelection } from '../FondoPicker';
import type { ModelSelection } from '../ModelPicker';
import { MedidasProductoBlock } from './MedidasProductoBlock';
import { AtributosProductoBlock } from './AtributosProductoBlock';
import {
  InfografiaConfigSheet,
  INFOGRAFIA_CONFIG_DEFAULT,
  type InfografiaConfig,
} from './InfografiaConfigSheet';
import {
  categoriaAdmiteMedidas,
  fetchAtributosYCategoria,
  fetchMedidasPorVariante,
  saveAtributosDeVariante,
  saveMedidasDeVariante,
} from '../medidasApi';
import { tieneMedidas, type Medidas } from '../../../lib/medidas';
import {
  VISTAS_MUEBLES,
  VISTAS_QUE_EXIGEN_MEDIDAS,
  VISTA_MUEBLE_CONTEXTO,
  VISTA_MUEBLE_INFOGRAFIA,
} from '../vistasMuebles';
import { MARCO_PANTALLA, BORDE_PANTALLA } from '../../../components/PantallaChrome';

export interface CatalogoCard {
  vista: VistaCatalogo;
  product: MockProductV2;
  /** Clave única para identificar la combinación. */
  key: string;
  /** Productos a combinar en esta imagen, ORDENADOS — el primero es el
   *  principal (p. ej. [remera, pantalón]). Viajan en `variante_id`, igual
   *  que el modo multi-producto de modelo. */
  comboProducts?: MockProductV2[];
  /**
   * Medidas del producto al momento de generar (modo muebles). Viajan acá y no
   * se re-consultan en el handler a propósito: lo que se manda al prompt tiene
   * que ser EXACTAMENTE lo que el usuario está viendo en pantalla, aunque el
   * guardado con debounce todavía no haya llegado a la base.
   */
  medidas?: Medidas | null;
  /** Cuántas imágenes generar de esta card. 1 = solo la base. >1 encadena el
   *  carrusel para las tomas alternativas. Solo "Foto en contexto" lo usa. */
  cantidad?: number;
  /** Config global de la infografía (modo muebles). Se adjunta a todas las
   *  cards por simplicidad; solo la usa la vista de infografía. */
  infografiaConfig?: InfografiaConfig;
  /** Características curadas del producto (texto libre, máx 3) para la
   *  infografía. Viajan al payload como `atributos_infografia`; n8n las dibuja
   *  cuando `mostrar_atributos` está activo. Ver ADR-808. */
  atributosInfografia?: string[];
}

export interface CatalogoCardsFullscreenProps {
  open: boolean;
  variant: 'mobile' | 'desktop';
  sidebarWidth: number;
  selectedProducts: MockProductV2[];
  selectedVistas: VistaCatalogo[];
  /** Catálogo de poses (incluye 1-persona y multi) + poses custom del
   *  muñeco — para el "+" que agrega una pose a un producto en este paso.
   *  Mismos datos que recibe VistaPickerFullscreen. */
  allPoses: PoseV2[];
  customPoses: PoseV2[];
  onBack: () => void;
  onGenerate: (cards: CatalogoCard[]) => void;
  // ── Props para el sheet de productos secundarios (combinar / outfit) ──
  userCompanyId: string | null;
  brandId: string | null;
  rubro: string | null;
  /** Plan del usuario — viaja al sheet, que lo usa para el tope del popup
   *  "Subir varios productos" (ADR-684). Acá el sheet abre en `directToPick`,
   *  así que hoy ese popup no es alcanzable; lo pasamos igual para que el
   *  límite no quede adivinado si mañana se habilita la pantalla Choose. */
  userPlan: string | null;
  tiposProductoSugeridos?: string[] | null;
  onProductsRefreshed: () => void;
  // ── Fondo + Modelo globales de la tanda ──────────────────────────────
  // Selecciones que aplican a TODAS las generaciones del catálogo. Los
  // botones abren los pickers existentes (mismos del modo modelo).
  fondoSelection: FondoSelection | null;
  modelSelection: ModelSelection | null;
  onOpenFondo: () => void;
  onOpenModel: () => void;
  /**
   * Modo "Catálogo de muebles": agrega el bloque de medidas por producto y
   * esconde lo que no aplica (Editar outfit y el botón de Modelo — no hay
   * poses ni modelo en este flujo). Default false → el Catálogo IA no cambia.
   */
  modoMuebles?: boolean;
  /** Modo Infografía (ADR-813): la única vista es la infografía → oculta la card
   *  "Agregar vista" (no se pueden sumar packshot/contexto en este modo). */
  soloInfografia?: boolean;
}

export const CatalogoCardsFullscreen: React.FC<CatalogoCardsFullscreenProps> = ({
  open,
  variant,
  sidebarWidth,
  selectedProducts,
  selectedVistas,
  allPoses,
  customPoses,
  onBack,
  onGenerate,
  userCompanyId,
  brandId,
  rubro,
  userPlan,
  tiposProductoSugeridos,
  onProductsRefreshed,
  fondoSelection,
  modelSelection,
  onOpenFondo,
  onOpenModel,
  modoMuebles = false,
  soloInfografia = false,
}) => {
  // Animación de entrada — misma que VistaPickerFullscreen.
  const [entered, setEntered] = useState(false);
  useEffect(() => {
    if (!open) {
      setEntered(false);
      return;
    }
    const id = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(id);
  }, [open]);

  // Conjunto inicial de cards (vista × producto compatible). Se
  // recalcula cuando cambian las selecciones. `removedKeys` lleva las
  // que el user descartó manualmente con la X.
  const allCards = React.useMemo<CatalogoCard[]>(() => {
    const cards: CatalogoCard[] = [];
    for (const vista of selectedVistas) {
      for (const product of selectedProducts) {
        // Tanto poses (con-modelo) como vistas solo-producto se combinan
        // SOLO con los productos compatibles: una pose que enfoca los pies
        // va con el zapato, no con la remera.
        if (isVistaCompatibleConModelView(vista, product.modelView)) {
          cards.push({
            vista,
            product,
            key: `${vista.kind}-${vista.id}__${product.id}`,
          });
        }
      }
    }
    return cards;
  }, [selectedVistas, selectedProducts]);

  // ── Poses AGREGADAS por producto (el "+"). Simétrico con `removedKeys`:
  // `removedKeys` saca cards; `addedVistasByProduct` suma una pose puntual
  // a un producto (la que el user se olvidó en el paso de poses). La card
  // nueva hereda el outfit del producto vía `effectiveCombo`. ────────────
  const [addedVistasByProduct, setAddedVistasByProduct] = useState<
    Record<string, VistaCatalogo[]>
  >({});
  // Poses (normales 1-persona + custom del muñeco) como VistaCatalogo, para
  // el picker "Agregar pose". Mismo mapeo que VistaPickerFullscreen.
  const posesAsVistas = React.useMemo<VistaCatalogo[]>(
    () =>
      allPoses
        .filter((p) => (p.cantidadPersonas ?? 1) === 1)
        .map((p) => ({
          id: p.id,
          nombre: p.nombre,
          imageUrl: p.imageUrl,
          kind: 'con-modelo' as const,
          areasPrincipales: p.areasPrincipales,
        })),
    [allPoses],
  );
  const customPosesAsVistas = React.useMemo<VistaCatalogo[]>(
    () =>
      customPoses.map((p) => ({
        id: p.id,
        nombre: p.nombre,
        imageUrl: p.imageUrl,
        kind: 'mis-pose' as const,
        filename: p.filename,
      })),
    [customPoses],
  );
  // Cards extra a partir de las poses agregadas. NO se filtra por
  // compatibilidad: el "+" muestra TODAS las poses y el user elige a
  // propósito (puede agregar una pose no "ideal" para ese producto).
  const addedCards = React.useMemo<CatalogoCard[]>(() => {
    const cards: CatalogoCard[] = [];
    for (const product of selectedProducts) {
      for (const vista of addedVistasByProduct[product.id] ?? []) {
        cards.push({
          vista,
          product,
          key: `${vista.kind}-${vista.id}__${product.id}`,
        });
      }
    }
    return cards;
  }, [addedVistasByProduct, selectedProducts]);
  // Baseline (cartesiano) + agregadas, deduplicado por key.
  const allCardsAll = React.useMemo<CatalogoCard[]>(() => {
    const map = new Map<string, CatalogoCard>();
    for (const c of allCards) map.set(c.key, c);
    for (const c of addedCards) if (!map.has(c.key)) map.set(c.key, c);
    return [...map.values()];
  }, [allCards, addedCards]);
  // Limpieza de `addedVistasByProduct`: saca productos deseleccionados y
  // poses que pasaron a ser baseline (ya están en `selectedVistas`).
  useEffect(() => {
    setAddedVistasByProduct((prev) => {
      const validPids = new Set(selectedProducts.map((p) => p.id));
      const next: Record<string, VistaCatalogo[]> = {};
      let changed = false;
      for (const [pid, vistas] of Object.entries(prev)) {
        if (!validPids.has(pid)) {
          changed = true;
          continue;
        }
        const kept = vistas.filter(
          (v) =>
            !selectedVistas.some((sv) => sv.kind === v.kind && sv.id === v.id),
        );
        if (kept.length !== vistas.length) changed = true;
        if (kept.length > 0) next[pid] = kept;
        else changed = true;
      }
      return changed ? next : prev;
    });
  }, [selectedProducts, selectedVistas]);

  const [removedKeys, setRemovedKeys] = useState<Set<string>>(new Set());
  // Al cambiar las selecciones, limpiamos las descartadas que ya no
  // existen en `allCardsAll` (sino se acumulan).
  useEffect(() => {
    setRemovedKeys((prev) => {
      const validKeys = new Set(allCardsAll.map((c) => c.key));
      const next = new Set<string>();
      for (const k of prev) if (validKeys.has(k)) next.add(k);
      return next;
    });
  }, [allCardsAll]);
  const activeCards = React.useMemo(
    () => allCardsAll.filter((c) => !removedKeys.has(c.key)),
    [allCardsAll, removedKeys],
  );

  // ── "+" Agregar pose: producto target + poses agregables ───────────────
  const [addPoseProductId, setAddPoseProductId] = useState<string | null>(null);
  const addPoseProduct =
    selectedProducts.find((p) => p.id === addPoseProductId) ?? null;
  // Agregables = TODAS las poses (1 persona + custom) cuyo card todavía NO
  // está presente en ese producto. NO se filtra por compatibilidad: el user
  // pidió ver todas y elegir la que quiera (las 20 poses tienen
  // `areasPrincipales`, así que filtrar por producto escondía muchas).
  //
  // En MUEBLES la fuente son las 3 vistas del modo, no las poses de modelo:
  // ofrecer poses acá no tenía ningún sentido (una pose de modelo para un
  // ropero). Y como son solo 3, cuando ya están todas la card "+" desaparece
  // en vez de abrir un sheet vacío.
  const vistasAgregablesPara = React.useCallback(
    (productId: string): VistaCatalogo[] => {
      const present = new Set(
        activeCards.filter((c) => c.product.id === productId).map((c) => c.key),
      );
      const fuente = modoMuebles
        ? VISTAS_MUEBLES
        : [...posesAsVistas, ...customPosesAsVistas];
      return fuente.filter(
        (v) => !present.has(`${v.kind}-${v.id}__${productId}`),
      );
    },
    [modoMuebles, posesAsVistas, customPosesAsVistas, activeCards],
  );

  const addablePoses = React.useMemo<VistaCatalogo[]>(
    () => (addPoseProduct ? vistasAgregablesPara(addPoseProduct.id) : []),
    [addPoseProduct, vistasAgregablesPara],
  );

  const removeCard = (key: string) =>
    setRemovedKeys((prev) => {
      const next = new Set(prev);
      next.add(key);
      return next;
    });

  // Agrega una pose a un producto desde el "+". Si la pose era baseline y
  // estaba borrada con la X → la des-borra; si es nueva → la suma a
  // `addedVistasByProduct`. La card resultante hereda el outfit del producto.
  const addPoseToProduct = (vista: VistaCatalogo, productId: string) => {
    const key = `${vista.kind}-${vista.id}__${productId}`;
    setRemovedKeys((prev) => {
      if (!prev.has(key)) return prev;
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
    const isBaseline = selectedVistas.some(
      (v) => v.kind === vista.kind && v.id === vista.id,
    );
    if (!isBaseline) {
      setAddedVistasByProduct((prev) => {
        const list = prev[productId] ?? [];
        if (list.some((v) => v.kind === vista.kind && v.id === vista.id))
          return prev;
        return { ...prev, [productId]: [...list, vista] };
      });
    }
  };

  // Agrupamos las cards activas POR PRODUCTO (en el orden en que el user
  // seleccionó los productos), para que las vistas de un mismo producto
  // queden juntas en vez de intercaladas. Solo entran productos que
  // todavía tienen al menos una vista activa.
  const groupedByProduct = React.useMemo(
    () =>
      selectedProducts
        .map((product) => ({
          product,
          cards: activeCards.filter((c) => c.product.id === product.id),
        }))
        .filter((g) => g.cards.length > 0),
    [selectedProducts, activeCards],
  );

  // ── Productos combinados (outfit) ───────────────────────────────────
  // Cada card combina uno o varios productos en una sola imagen. La lista
  // va ORDENADA: el primero es el PRINCIPAL. `productCombos` lo setea
  // "Editar outfit" (aplica a todas las poses del producto); `cardCombos`
  // pisa una card puntual. Combo efectivo = card ?? producto ?? [principal].
  const [productCombos, setProductCombos] = useState<
    Record<string, MockProductV2[]>
  >({});
  const [cardCombos, setCardCombos] = useState<
    Record<string, MockProductV2[]>
  >({});
  // Qué estamos editando cuando el sheet está abierto.
  const [sheetTarget, setSheetTarget] = useState<
    | { type: 'card'; key: string; productId: string }
    | { type: 'product'; productId: string }
    | null
  >(null);

  const effectiveCombo = (card: CatalogoCard): MockProductV2[] =>
    cardCombos[card.key] ?? productCombos[card.product.id] ?? [card.product];

  // Selección inicial del sheet según el target (combo actual, principal
  // primero).
  const sheetSelection: MockProductV2[] = (() => {
    if (!sheetTarget) return [];
    if (sheetTarget.type === 'card') {
      const card = activeCards.find((c) => c.key === sheetTarget.key);
      return card ? effectiveCombo(card) : [];
    }
    const product = selectedProducts.find(
      (p) => p.id === sheetTarget.productId,
    );
    return productCombos[sheetTarget.productId] ?? (product ? [product] : []);
  })();

  const handleSheetConfirm = (products: MockProductV2[]) => {
    if (!sheetTarget) return;
    if (sheetTarget.type === 'product') {
      const pid = sheetTarget.productId;
      setProductCombos((prev) => ({ ...prev, [pid]: products }));
      // "Editar outfit" es autoritativo para todas las poses del producto:
      // limpiamos overrides por card de ese producto (su key termina en
      // `__<productId>`) para que el outfit aplique parejo.
      setCardCombos((prev) => {
        const next = { ...prev };
        for (const k of Object.keys(next)) {
          if (k.endsWith(`__${pid}`)) delete next[k];
        }
        return next;
      });
    } else {
      const key = sheetTarget.key;
      setCardCombos((prev) => ({ ...prev, [key]: products }));
    }
    setSheetTarget(null);
  };

  // ── Configuración GLOBAL de la infografía ────────────────────────────
  // Global y no por producto a propósito: son decisiones de estilo de la
  // tanda. Viajan como dos booleanos en el payload y el prompt de n8n arma o
  // no arma cada bloque. Ver InfografiaConfigSheet.
  const [infografiaConfig, setInfografiaConfig] = useState<InfografiaConfig>(
    INFOGRAFIA_CONFIG_DEFAULT,
  );
  const [infografiaSheetOpen, setInfografiaSheetOpen] = useState(false);
  // ── Override de infografía POR PRODUCTO ──────────────────────────────────
  // Cada entrada guarda SOLO los campos que el usuario cambió (override
  // parcial); el resto hereda `infografiaConfig` ("heredás lo que no tocás").
  // Presencia de la key = producto personalizado. Ver ADR-808.
  const [infografiaOverride, setInfografiaOverride] = useState<
    Record<string, Partial<InfografiaConfig>>
  >({});
  /** Producto cuyo sheet de infografía por producto está abierto (o null). */
  const [infografiaProductoOpen, setInfografiaProductoOpen] = useState<
    string | null
  >(null);
  /** Config efectiva de un producto = global ⊕ su override parcial. */
  const configInfografiaDe = React.useCallback(
    (productId: string): InfografiaConfig => ({
      ...infografiaConfig,
      ...(infografiaOverride[productId] ?? {}),
    }),
    [infografiaConfig, infografiaOverride],
  );
  const esProductoPersonalizado = (productId: string) =>
    infografiaOverride[productId] !== undefined;
  const togglePersonalizarProducto = (productId: string, on: boolean) =>
    setInfografiaOverride((prev) => {
      const next = { ...prev };
      if (on) next[productId] = prev[productId] ?? {};
      else delete next[productId];
      return next;
    });
  // El sheet devuelve la config EFECTIVA completa; guardamos solo lo que difiere
  // del global para que el resto siga heredando.
  const handleProductoConfigChange = (
    productId: string,
    next: InfografiaConfig,
  ) =>
    setInfografiaOverride((prev) => {
      const ov: Partial<InfografiaConfig> = {};
      if (next.estilo !== infografiaConfig.estilo) ov.estilo = next.estilo;
      if (next.color !== infografiaConfig.color) ov.color = next.color;
      if (next.mostrarMedidas !== infografiaConfig.mostrarMedidas)
        ov.mostrarMedidas = next.mostrarMedidas;
      if (next.mostrarAtributos !== infografiaConfig.mostrarAtributos)
        ov.mostrarAtributos = next.mostrarAtributos;
      return { ...prev, [productId]: ov };
    });
  // Características curadas por producto (texto libre, máx 3). Independiente del
  // override de estilo. Hoy se cargan a mano; con el backend nuevo se
  // pre-cargan por IA y n8n las dibuja. Ver ADR-808.
  const [atributosPorProducto, setAtributosPorProducto] = useState<
    Record<string, string[]>
  >({});
  /** La card de config solo tiene sentido si hay alguna infografía en la tanda. */
  const hayInfografia = React.useMemo(
    () =>
      modoMuebles &&
      activeCards.some((c) => c.vista.id === VISTA_MUEBLE_INFOGRAFIA),
    [modoMuebles, activeCards],
  );

  /** Texto corto del estado, para la card global. */
  const resumenInfografia = (() => {
    const { mostrarMedidas, mostrarAtributos } = infografiaConfig;
    if (mostrarMedidas && mostrarAtributos) return 'Medidas y características';
    if (mostrarMedidas) return 'Solo medidas';
    if (mostrarAtributos) return 'Solo características';
    return 'Sin anotaciones';
  })();

  // ── Cantidad de imágenes por card (solo "Foto en contexto") ──────────
  // 1 = solo la imagen base. >1 = la base + (n-1) tomas alternativas vía
  // carrusel (mismo mueble, mismo ambiente, otro ángulo).
  //
  // TOPE 3, y no es arbitrario: el carrusel pide `cantidad_imagenes = n - 1`,
  // así que con 3 pide 2 — el máximo que el workflow `Generar nuevos angulos`
  // maneja bien hoy. Con más, su nodo `get gen images3` tiene `limit: 2`
  // hardcodeado mientras la condición de corte exige `>= cantidad_imagenes`,
  // así que loopea hasta el timeout de 180s. Subir este tope SOLO después de
  // arreglar eso en n8n.
  const CANTIDAD_MIN = 1;
  const CANTIDAD_MAX = 3;
  const [cantidadPorCard, setCantidadPorCard] = useState<
    Record<string, number>
  >({});
  const cantidadDe = (key: string) => cantidadPorCard[key] ?? 1;
  const setCantidad = (key: string, n: number) =>
    setCantidadPorCard((prev) => ({
      ...prev,
      [key]: Math.min(CANTIDAD_MAX, Math.max(CANTIDAD_MIN, n)),
    }));
  /** Solo la foto en contexto admite varias tomas: el packshot y la
   *  infografía son una imagen única por definición. */
  const admiteCantidad = (card: CatalogoCard) =>
    modoMuebles && card.vista.id === VISTA_MUEBLE_CONTEXTO;

  /** Total de imágenes que se van a generar = cards + extras del stepper. */
  const totalImagenes = React.useMemo(
    () =>
      activeCards.reduce(
        (acc, c) => acc + (admiteCantidad(c) ? cantidadDe(c.key) : 1),
        0,
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeCards, cantidadPorCard, modoMuebles],
  );

  // ── Medidas por producto (solo modo muebles) ─────────────────────────
  // Indexadas por id de VARIANTE, que es lo que maneja la UI. medidasApi
  // traduce a producto padre al leer y al guardar (las variantes de color de un
  // mismo mueble comparten medidas).
  const [medidasPorVariante, setMedidasPorVariante] = useState<
    Record<string, Medidas | null>
  >({});
  const [medidasCargando, setMedidasCargando] = useState(false);
  const [medidasError, setMedidasError] = useState<string | null>(null);
  // Categoría del producto (la clasifica la IA al subir). Indexada por variante, como
  // las medidas. Decide si se piden/muestran medidas (herramienta/decoración → no).
  const [categoriaPorVariante, setCategoriaPorVariante] = useState<
    Record<string, string | null>
  >({});

  useEffect(() => {
    if (!open || !modoMuebles) return;
    const ids = selectedProducts.map((p) => p.id);
    if (ids.length === 0) return;
    let cancelado = false;
    setMedidasCargando(true);
    void fetchMedidasPorVariante(ids)
      .then((res) => {
        if (cancelado) return;
        // Merge, no reemplazo: si el user ya editó algo en esta sesión, lo que
        // escribió gana sobre lo que devuelve la DB (que puede estar viejo por
        // el debounce del guardado).
        setMedidasPorVariante((prev) => ({ ...res, ...prev }));
      })
      .finally(() => {
        if (!cancelado) setMedidasCargando(false);
      });
    // Atributos (precarga de la IA) + categoría, en paralelo. No bloquean nada:
    // si no hay, la UI queda como hoy (atributos vacíos, medidas visibles).
    void fetchAtributosYCategoria(ids).then((res) => {
      if (cancelado) return;
      setCategoriaPorVariante((prev) => ({ ...res.categoria, ...prev }));
      // Precarga SOLO los productos que el user todavía no tocó en esta sesión;
      // lo que editó a mano gana (misma regla que las medidas).
      setAtributosPorProducto((prev) => {
        const merged = { ...prev };
        for (const [id, attrs] of Object.entries(res.atributos)) {
          if (!(id in merged) && attrs.length > 0) merged[id] = attrs;
        }
        return merged;
      });
    });
    return () => {
      cancelado = true;
    };
    // `selectedProducts` cambia de identidad seguido; dependemos de los ids.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, modoMuebles, selectedProducts.map((p) => p.id).join(',')]);

  // Guardado con debounce por variante. Un timer POR producto (no uno global)
  // para que editar dos productos seguidos no cancele el guardado del primero.
  const saveTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  useEffect(
    () => () => {
      // Al desmontar, limpiar timers pendientes. Lo que no llegó a guardarse se
      // pierde — aceptable: son 600ms y el user sigue en la pantalla mientras
      // edita.
      Object.values(saveTimers.current).forEach((t) => clearTimeout(t));
    },
    [],
  );

  const handleMedidasChange = (varianteId: string, next: Medidas) => {
    setMedidasPorVariante((prev) => ({ ...prev, [varianteId]: next }));
    setMedidasError(null);
    clearTimeout(saveTimers.current[varianteId]);
    saveTimers.current[varianteId] = setTimeout(() => {
      void saveMedidasDeVariante(varianteId, next).then((r) => {
        if (!r.ok) {
          setMedidasError(
            'No se pudieron guardar las medidas. Revisá tu conexión.',
          );
        }
      });
    }, 600);
  };

  // Atributos: además de actualizar el estado, PERSISTE a variantes.atributos con debounce
  // (mismo patrón que las medidas). Así lo que el cliente cura queda para la próxima sesión;
  // se guardan solo los no vacíos. Key `atr:` para no pisar el timer de medidas del producto.
  const handleAtributosChange = (varianteId: string, next: string[]) => {
    setAtributosPorProducto((prev) => ({ ...prev, [varianteId]: next }));
    clearTimeout(saveTimers.current[`atr:${varianteId}`]);
    saveTimers.current[`atr:${varianteId}`] = setTimeout(() => {
      void saveAtributosDeVariante(
        varianteId,
        next.map((a) => a.trim()).filter(Boolean),
      );
    }, 600);
  };

  // ¿Este PRODUCTO exige medidas cargadas? Depende de las vistas elegidas y —en
  // la infografía— de su config EFECTIVA (global ⊕ override por-producto): el
  // usuario puede prender/apagar las cotas producto por producto. Si la
  // infografía no traza cotas (o este producto las apagó), no se exigen: un foco
  // o una herramienta puede no tener ninguna. Las medidas que igual estén
  // cargadas se siguen mandando como contexto a la IA. Ver ADR-806 + ADR-808.
  const productoExigeMedidas = React.useCallback(
    (productId: string) =>
      // Si la categoría no se vende por tamaño (herramienta/decoración) NUNCA se
      // exigen medidas, aunque la vista las pediría (ADR-808).
      categoriaAdmiteMedidas(categoriaPorVariante[productId]) &&
      selectedVistas.some(
        (v) =>
          VISTAS_QUE_EXIGEN_MEDIDAS.has(v.id) &&
          (v.id !== VISTA_MUEBLE_INFOGRAFIA ||
            configInfografiaDe(productId).mostrarMedidas),
      ),
    [selectedVistas, configInfografiaDe, categoriaPorVariante],
  );

  /** Productos que bloquean el generar: la vista lo exige (según su config
   *  EFECTIVA por-producto) y no cargaron ninguna medida. */
  const productosSinMedidas = React.useMemo(() => {
    if (!modoMuebles || medidasCargando) return [];
    return groupedByProduct
      .map(({ product }) => product)
      .filter(
        (p) =>
          productoExigeMedidas(p.id) && !tieneMedidas(medidasPorVariante[p.id]),
      );
  }, [
    modoMuebles,
    medidasCargando,
    groupedByProduct,
    medidasPorVariante,
    productoExigeMedidas,
  ]);

  // Adjunta el combo efectivo (principal + combinados) a cada card antes de
  // generar — el padre lo manda en `variante_id`, igual que multi-producto
  // del modo modelo.
  const handleGenerate = () => {
    // Guard de medidas: en vez de deshabilitar el botón (que no explica nada),
    // se scrollea y sacude el primer producto incompleto — mismo patrón que el
    // picker de vistas cuando no elegiste ninguna (ADR-555).
    if (productosSinMedidas.length > 0) {
      scrollToAndShake(`medidas-${productosSinMedidas[0].id}`);
      return;
    }
    onGenerate(
      activeCards.map((c) => ({
        ...c,
        comboProducts: effectiveCombo(c),
        medidas: modoMuebles ? (medidasPorVariante[c.product.id] ?? null) : null,
        cantidad: admiteCantidad(c) ? cantidadDe(c.key) : 1,
        infografiaConfig: modoMuebles
          ? // Si la categoría no se vende por tamaño (herramienta/decoración), la
            // infografía NO debe trazar cotas: forzamos mostrarMedidas=false para que el
            // prompt no dibuje líneas de cota vacías cuando el producto no tiene medidas.
            categoriaAdmiteMedidas(categoriaPorVariante[c.product.id])
            ? configInfografiaDe(c.product.id)
            : { ...configInfografiaDe(c.product.id), mostrarMedidas: false }
          : undefined,
        atributosInfografia: modoMuebles
          ? (atributosPorProducto[c.product.id]?.filter((a) => a.trim()) ?? [])
          : undefined,
      })),
    );
  };

  // Labels de los botones globales Fondo / Modelo (default blanco / IA).
  const fondoLabel = (() => {
    const s = fondoSelection;
    if (!s || s.mode === 'blanco') return 'Blanco';
    if (s.mode === 'auto') return 'Automático';
    if (s.mode === 'biblioteca' || s.mode === 'guardado') return s.name;
    return 'Personalizado';
  })();
  const modeloLabel = (() => {
    const s = modelSelection;
    if (!s || s.mode === 'auto') return 'Automático';
    if (s.mode === 'preset') return s.name;
    if (s.mode === 'custom') return 'Personalizado';
    return 'Subido';
  })();
  // El botón de Modelo aplica si hay poses (con-modelo o mis-pose) en la
  // tanda — ambas generan CON modelo. Las solo-producto no usan modelo.
  // En muebles NUNCA hay modelo: las 3 vistas son solo-producto, así que el
  // botón global de Modelo no tiene sentido y se esconde.
  const hasModelo =
    !modoMuebles && activeCards.some((c) => c.vista.kind !== 'solo-producto');
  // Y tampoco hay selector global de Fondo: en muebles CADA VISTA define su
  // propio fondo (el packshot es ciclorama blanco, la foto en contexto es un
  // ambiente). Un selector global se contradiría con la vista elegida.
  const hasFondo = !modoMuebles;
  // Si no hay ni Fondo ni Modelo, el bloque de ajustes globales no va.
  // En muebles el bloque vuelve pero con otro contenido: la config de la
  // infografía (mismo lugar y mismo patrón que Fondo/Modelo, que es lo que se
  // pidió — "opciones globales arriba y por producto individualmente").
  const hasAjustesGlobales = hasFondo || hasModelo || hayInfografia;

  // Datos para el thumbnail de las cards Fondo/Modelo en desktop.
  const fondoIsAuto = fondoSelection?.mode === 'auto';
  const fondoIsBlanco = !fondoSelection || fondoSelection.mode === 'blanco';
  const fondoImg =
    fondoSelection?.mode === 'biblioteca' ||
    fondoSelection?.mode === 'guardado'
      ? fondoSelection.imageUrl
      : null;
  const modeloIsAuto = !modelSelection || modelSelection.mode === 'auto';
  const modeloImg =
    modelSelection?.mode === 'preset'
      ? modelSelection.imageUrl
      : modelSelection?.mode === 'upload'
        ? modelSelection.previewUrl
        : null;

  const isMobile = variant === 'mobile';

  if (!open) return null;

  // Separador de grupo — thumb + nombre del producto, ocupando toda la
  // fila del grid. Es lo que hace visible la clasificación por producto,
  // igual que el catálogo de GeneratedImagesInline.
  const renderProductHeader = (product: MockProductV2) => {
    // Combinados extra del producto (combo menos el principal).
    const extra = Math.max(0, (productCombos[product.id]?.length ?? 1) - 1);
    return (
      <div
        key={`header-${product.id}`}
        className="col-span-full flex items-center gap-2.5 pt-3 pb-1 first:pt-0"
      >
        <CachedImg
          src={product.imageUrl}
          alt={product.name}
          className="w-10 h-10 rounded-lg object-cover ring-1 ring-black/[0.06] shrink-0"
        />
        <span className="min-w-0 max-w-max text-[15px] font-semibold text-brand-black/90 truncate">
          {product.name}
        </span>
        {/* "Editar outfit" — justo a la derecha del título. Productos
            secundarios para TODAS las poses de este producto. */}
        <button
          type="button"
          onClick={() => setSheetTarget({ type: 'product', productId: product.id })}
          // "Editar outfit" no aplica a muebles: no se combinan prendas. La
          // combinación de varios muebles en una escena es otra cosa y queda
          // fuera de la v1.
          hidden={modoMuebles}
          className={`ml-auto sm:ml-0 shrink-0 items-center gap-1.5 px-3 py-1.5 rounded-full border border-neutral-200 bg-white hover:bg-neutral-50 active:scale-95 text-[12.5px] font-medium text-brand-black/80 transition-all ${
            modoMuebles ? 'hidden' : 'inline-flex'
          }`}
        >
          <Shirt className="w-3.5 h-3.5" />
          <span>Editar outfit</span>
          {extra > 0 && (
            <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-brand-primary text-white text-[11px] font-semibold">
              +{extra}
            </span>
          )}
        </button>
      </div>
    );
  };

  // ¿Es la última pose activa de su producto? No se puede borrar — cada
  // producto debe quedar con al menos una pose (si no, su grupo desaparece
  // y queda vacío).
  const isLastPoseOfProduct = (card: CatalogoCard) =>
    activeCards.filter((c) => c.product.id === card.product.id).length <= 1;

  /** Stepper +/- de la card "Foto en contexto". Mismo patrón que el de
   *  cantidad del ConfigurePanel: botones circulares w-8 h-8, tabular-nums y
   *  ancho fijo para que el número no salte al cambiar. */
  const renderCantidadStepper = (card: CatalogoCard) => {
    const n = cantidadDe(card.key);
    return (
      <div className="mt-auto min-h-[45px] flex items-center justify-between gap-2 px-3 py-2 border-t border-neutral-200 bg-neutral-50/60">
        <span className="text-[12px] text-[#3C3C43]/70 truncate">
          {n === 1 ? '1 imagen' : `${n} imágenes`}
        </span>
        <div className="inline-flex items-center gap-1.5 shrink-0">
          <button
            type="button"
            onClick={() => setCantidad(card.key, n - 1)}
            disabled={n <= CANTIDAD_MIN}
            aria-label="Una imagen menos"
            className="w-7 h-7 inline-flex items-center justify-center rounded-full border border-neutral-200 bg-white text-brand-black/70 hover:border-brand-primary/40 hover:text-brand-primary disabled:opacity-40 disabled:pointer-events-none transition-colors"
          >
            <Minus className="w-3.5 h-3.5" />
          </button>
          <span className="min-w-[16px] text-center text-[13px] font-semibold text-brand-black/90 tabular-nums">
            {n}
          </span>
          <button
            type="button"
            onClick={() => setCantidad(card.key, n + 1)}
            disabled={n >= CANTIDAD_MAX}
            aria-label="Una imagen más"
            className="w-7 h-7 inline-flex items-center justify-center rounded-full border border-neutral-200 bg-white text-brand-black/70 hover:border-brand-primary/40 hover:text-brand-primary disabled:opacity-40 disabled:pointer-events-none transition-colors"
          >
            <Plus className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    );
  };

  /** Footer de la card "Infografía": abre el sheet de override por producto.
   *  Va DENTRO de la card (mismo patrón que el stepper de cantidad), no en una
   *  fila aparte. */
  const renderEditarInfografiaBtn = (card: CatalogoCard) => {
    const personalizada = esProductoPersonalizado(card.product.id);
    return (
      <button
        type="button"
        onClick={() => setInfografiaProductoOpen(card.product.id)}
        className="mt-auto w-full min-h-[45px] flex items-center justify-center gap-1.5 px-3 py-2 border-t border-neutral-200 bg-neutral-50/60 text-[12.5px] font-medium text-brand-primary hover:bg-brand-primary/[0.04] transition-colors"
      >
        <Pencil className="w-3.5 h-3.5" />
        {personalizada ? 'Infografía personalizada' : 'Editar infografía'}
        {personalizada && (
          <span
            className="w-1.5 h-1.5 rounded-full bg-brand-primary"
            aria-hidden
          />
        )}
      </button>
    );
  };

  const renderCard = (card: CatalogoCard) => (
    <div
      key={card.key}
      className="relative flex flex-col rounded-2xl border border-neutral-200 bg-white overflow-hidden animate-mode-swap"
    >
      {/* X — esquina superior derecha. Deshabilitada en la última pose del
          producto (cada producto necesita al menos una). */}
      <button
        type="button"
        onClick={() => removeCard(card.key)}
        disabled={isLastPoseOfProduct(card)}
        title={
          isLastPoseOfProduct(card)
            ? 'Cada producto necesita al menos una pose'
            : undefined
        }
        aria-label={`Quitar ${card.vista.nombre} para ${card.product.name}`}
        className={`absolute top-2.5 right-2.5 z-10 w-7 h-7 inline-flex items-center justify-center rounded-full backdrop-blur-sm transition-all ${
          isLastPoseOfProduct(card)
            ? 'bg-black/20 text-white/50 cursor-not-allowed'
            : 'bg-black/55 text-white hover:bg-black/70 active:scale-95'
        }`}
      >
        <X className="w-3.5 h-3.5" strokeWidth={2.5} />
      </button>
      <div className="flex items-stretch gap-3 p-3">
        {/* Imagen de la vista (mitad izquierda). Resolvemos con el
            model_view del producto de ESTA card específica, así cada
            (vista × producto) muestra el thumb correcto cuando hay un
            override (ropa). */}
        <div className="shrink-0 w-1/2 aspect-[3/4] rounded-lg overflow-hidden bg-neutral-100">
          {(() => {
            const thumb = resolveVistaImageUrl(
              card.vista,
              card.product.modelView,
            );
            return thumb ? (
              <CachedImg
                src={thumb}
                alt={card.vista.nombre}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-[#3C3C43]/40 text-[11px] text-center px-2">
                {card.vista.nombre}
              </div>
            );
          })()}
        </div>
        {/* Info derecha: tipo + nombre vista + producto + thumb. */}
        <div className="flex-1 min-w-0 flex flex-col items-center justify-center gap-3 text-center">
          <div className="min-w-0 w-full">
            <p className="text-[10.5px] uppercase tracking-wide font-medium text-[#3C3C43]/55">
              {card.vista.kind === 'solo-producto' ? 'Vista' : 'Pose'}
            </p>
            {/* `line-clamp-2` parte el nombre largo en máximo 2 líneas
                y agrega ellipsis al final si todavía no cabe. */}
            <p
              className="text-[15px] font-semibold leading-snug text-brand-black/90 line-clamp-2 break-words"
              title={card.vista.nombre}
            >
              {card.vista.nombre}
            </p>
          </div>
          {/* Bloque de producto(s) tappable: abre el sheet para combinar
              productos. Cuando hay varios, se muestran STACKEADOS (mismo
              patrón que el chip de productos seleccionados en config). */}
          {(() => {
            const combo = effectiveCombo(card);
            const multiple = combo.length > 1;
            return (
              <button
                type="button"
                onClick={() =>
                  setSheetTarget({
                    type: 'card',
                    key: card.key,
                    productId: card.product.id,
                  })
                }
                className="flex flex-col items-center gap-1.5 rounded-lg px-1 py-1 -my-1 max-w-full hover:bg-black/[0.03] active:scale-[0.98] transition-all"
              >
                <p className="text-[10.5px] uppercase tracking-wide font-medium text-[#3C3C43]/55">
                  {multiple ? 'Productos' : 'Producto'}
                </p>
                {multiple ? (
                  <div className="flex items-center -space-x-2">
                    {combo.slice(0, 3).map((p) => (
                      <CachedImg
                        key={p.id}
                        src={p.imageUrl}
                        alt={p.name}
                        className="w-10 h-10 rounded-lg object-cover ring-2 ring-white bg-neutral-100"
                      />
                    ))}
                    {combo.length > 3 && (
                      <div className="w-10 h-10 rounded-lg bg-neutral-100 ring-2 ring-white inline-flex items-center justify-center text-[11px] font-semibold text-brand-black/70">
                        +{combo.length - 3}
                      </div>
                    )}
                  </div>
                ) : (
                  <CachedImg
                    src={combo[0].imageUrl}
                    alt={combo[0].name}
                    className="w-12 h-12 rounded-lg object-cover ring-1 ring-black/[0.06]"
                  />
                )}
                <p className="text-[12.5px] text-brand-black/80 leading-tight max-w-full truncate px-1">
                  {multiple
                    ? `${combo[0].name} y ${combo.length - 1} más`
                    : combo[0].name}
                </p>
              </button>
            );
          })()}
        </div>
      </div>
      {/* Stepper de cantidad — solo "Foto en contexto" (el packshot y la
          infografía son una imagen única por definición). */}
      {admiteCantidad(card) && renderCantidadStepper(card)}
      {/* Footer "Editar infografía" — DENTRO de la card de Infografía. */}
      {modoMuebles &&
        card.vista.id === VISTA_MUEBLE_INFOGRAFIA &&
        renderEditarInfografiaBtn(card)}
    </div>
  );

  /**
   * Tira de producto del modo Infografía (ADR-814 + rediseño de la llamada del
   * 2026-07-28). En ese modo la única vista es la infografía, así que la card
   * "Vista: Infografía" sobra: cada producto se muestra como UNA tira horizontal
   * full-width con la imagen grande, sus medidas + características editables
   * inline, y el botón "Editar infografía" a la vista. Reemplaza al trío
   * header + bloques + card del catálogo normal. La card de infografía sigue
   * viva en `activeCards` (esto es solo presentación), así que se genera igual.
   */
  const renderInfografiaProductStrip = (product: MockProductV2) => {
    const personalizada = esProductoPersonalizado(product.id);
    const admiteMed = categoriaAdmiteMedidas(categoriaPorVariante[product.id]);
    return (
      <div
        key={product.id}
        className="col-span-full rounded-2xl border border-neutral-200 bg-white p-4 animate-mode-swap"
      >
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Imagen grande del producto (izquierda en desktop). */}
          <CachedImg
            src={product.imageUrl}
            alt={product.name}
            className="shrink-0 w-full sm:w-44 aspect-square sm:self-start rounded-xl object-cover bg-neutral-100 ring-1 ring-black/[0.05]"
          />
          {/* Datos + edición (derecha, ocupa el resto). */}
          <div className="flex-1 min-w-0 flex flex-col gap-3.5">
            <div className="flex items-center gap-2">
              <span className="min-w-0 text-[16px] font-semibold text-brand-black/90 truncate">
                {product.name}
              </span>
              {personalizada && (
                <span className="shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-brand-primary/10 text-brand-primary text-[11px] font-medium">
                  <span className="w-1.5 h-1.5 rounded-full bg-brand-primary" />
                  Personalizada
                </span>
              )}
            </div>

            {/* Medidas — solo si la categoría se vende por tamaño (ADR-813). */}
            {modoMuebles && admiteMed && (
              <MedidasProductoBlock
                embedded
                anchorId={`medidas-${product.id}`}
                medidas={medidasPorVariante[product.id] ?? null}
                onChange={(next) => handleMedidasChange(product.id, next)}
                exigidas={
                  productoExigeMedidas(product.id) && !medidasCargando
                }
              />
            )}

            {/* Características editables inline (todas las categorías). */}
            {modoMuebles && (
              <AtributosProductoBlock
                embedded
                atributos={atributosPorProducto[product.id] ?? []}
                onChange={(next) => handleAtributosChange(product.id, next)}
              />
            )}

            {/* Botón de edición de estilo de la infografía, a la vista. */}
            <div className="mt-0.5">
              <button
                type="button"
                onClick={() => setInfografiaProductoOpen(product.id)}
                className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full border border-neutral-200 bg-white hover:bg-neutral-50 active:scale-95 text-[13px] font-medium text-brand-primary transition-all"
              >
                <Pencil className="w-3.5 h-3.5" />
                {/* Solo "Editar": ya se sabe que es la infografía (estás en el
                    modo). El estado "personalizada" lo comunica el badge de
                    arriba, no hace falta repetirlo acá. */}
                Editar
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Card "+" al final de cada grupo: abre el picker para AGREGAR una pose a
  // ESE producto. La card nueva hereda el outfit del producto.
  const renderAddPoseCard = (product: MockProductV2) => {
    // Modo Infografía (ADR-813): la única vista es la infografía → nunca se agregan
    // vistas, así que no va la card "Agregar vista".
    if (soloInfografia) return null;
    // Sin nada agregable no se muestra la card. En el Catálogo IA casi nunca
    // pasa (hay ~20 poses), pero en muebles con 3 vistas es el caso normal
    // apenas el user las elige todas.
    if (vistasAgregablesPara(product.id).length === 0) return null;
    const label = modoMuebles ? 'Agregar vista' : 'Agregar pose';
    return (
    <button
      key={`add-${product.id}`}
      type="button"
      onClick={() => setAddPoseProductId(product.id)}
      aria-label={`${label} para ${product.name}`}
      className="rounded-2xl border-2 border-dashed border-neutral-300 hover:border-brand-primary/50 bg-white hover:bg-brand-primary/[0.03] flex flex-col items-center justify-center gap-2 text-[#3C3C43]/55 hover:text-brand-primary transition-colors min-h-[150px] animate-mode-swap"
    >
      <span className="w-10 h-10 rounded-full border-2 border-current inline-flex items-center justify-center">
        <Plus className="w-5 h-5" strokeWidth={2.5} />
      </span>
      <span className="text-[13px] font-medium">{label}</span>
    </button>
    );
  };

  return (
    <div
      // El marco flotante sale de `PantallaChrome`, compartido con las pantallas de
      // modo y el asistente (ADR-2026-08-03-chrome-de-pantalla-compartido).
      // `right`/`top` salieron del `style` inline y pasaron a clases: un estilo
      // inline no se puede meter en una media query, así que ahí ningún `lg:` los
      // alcanzaba. `left` se queda inline porque depende de `isMobile`, y
      // `sidebarWidth` ya trae el aire de ese lado (margen + ancho + margen).
      className={`fixed inset-0 z-[110] bg-surface-mobile md:bg-surface-content md:rounded-tl-[26px] md:rounded-bl-[26px] md:overflow-hidden ${MARCO_PANTALLA} ${BORDE_PANTALLA} flex flex-col transition-transform duration-200 ease-out`}
      style={{
        left: isMobile ? 0 : sidebarWidth,
        // Sin fade de opacidad a propósito: el overlay tiene fondo OPACO. Si le
        // animábamos la opacidad del elemento entero, durante el fundido de
        // 200ms quedaba semi-transparente y se veía la home de atrás (las
        // imágenes de modelo de "Diseña tu imagen" colándose en la transición).
        // Solo deslizamos; el fondo tapa siempre lo de atrás.
        transform: entered ? 'translateY(0)' : 'translateY(8px)',
      }}
    >
      {/* Cobertura blanca sobre la zona del chrome flotante mobile. */}
      {isMobile && (
        <div
          aria-hidden="true"
          className="absolute inset-x-0 top-0 bg-white pointer-events-none z-[5]"
          style={{
            height: 'calc(env(safe-area-inset-top, 0px) + 3.5rem)',
          }}
        />
      )}

      {/* Desktop sticky top bar: Volver (left) + Generar (right). */}
      {!isMobile && (
        <div className="shrink-0 z-[20] px-8 pt-5 pb-4 flex items-center justify-between bg-white">
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center gap-1 text-brand-primary text-[14px] hover:opacity-75 transition-opacity"
          >
            <ChevronLeft className="w-4 h-4" />
            Volver
          </button>
          {/* Sin cards: el botón queda atenuado pero clickeable — muestra el
              hint en la card que asoma abajo y el click scrollea+sacude el
              empty-state (mismo patrón que el modo influencer, ADR-555). */}
          <div className="relative inline-flex flex-col items-stretch">
            <div
              aria-hidden
              className={`absolute inset-0 rounded-[22px] bg-white/85 backdrop-blur-md shadow-md ring-1 ring-black/[0.04] transition-opacity duration-300 ${
                activeCards.length === 0 ? 'opacity-100' : 'opacity-0'
              }`}
            />
            <div className="relative z-10">
              <button
                type="button"
                onClick={() => {
                  if (activeCards.length === 0) {
                    scrollToAndShake('catalogo-cards-zone');
                    return;
                  }
                  handleGenerate();
                }}
                aria-disabled={activeCards.length === 0}
                className={`w-full inline-flex items-center justify-center gap-2 py-2.5 pl-5 pr-2.5 rounded-full bg-brand-primary hover:bg-brand-primary-hover text-white text-[14.5px] font-semibold shadow-md transition-colors ${
                  activeCards.length === 0 ? 'opacity-60' : ''
                }`}
              >
                <Sparkles className="w-4 h-4" />
                <span>Generar</span>
                {activeCards.length > 0 && (
                  <span className="inline-flex items-center gap-1 bg-white text-brand-black px-2 py-0.5 rounded-full text-[12.5px] font-semibold ml-1">
                    <img
                      src="/images/token_icon.png"
                      alt=""
                      aria-hidden="true"
                      className="w-3.5 h-3.5 object-contain"
                    />
                    {totalImagenes}
                  </span>
                )}
              </button>
            </div>
            <div
              className={`relative z-10 w-0 min-w-full overflow-hidden transition-all duration-300 ease-out ${
                activeCards.length === 0
                  ? 'max-h-16 opacity-100'
                  : 'max-h-0 opacity-0'
              }`}
            >
              <p className="pt-2 pb-1.5 px-3 text-center text-[12.5px] leading-snug text-[#3C3C43]/70">
                Agregá al menos una vista o producto para generar.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Scroll area */}
      <div className="flex-1 overflow-y-auto">
        {/* Title — el id es el target del scroll+shake cuando se intenta
            generar sin cards activas (acá vive el empty-state que explica
            cómo sumar vistas/productos). ADR-555. */}
        <div
          id="catalogo-cards-zone"
          className="px-5 sm:px-8 pb-3 sm:pt-2"
          style={{
            paddingTop: isMobile
              ? 'calc(env(safe-area-inset-top, 0px) + 3.5rem + 1rem)'
              : undefined,
          }}
        >
          <h1 className="text-[22px] sm:text-[26px] font-semibold text-brand-black/90 leading-tight">
            Revisá tu catálogo
          </h1>
          <p className="text-[13.5px] sm:text-[14.5px] text-[#3C3C43]/60 mt-1">
            {activeCards.length === 0
              ? 'No quedan combinaciones para generar — agregá vistas o productos.'
              : `Vamos a generar ${totalImagenes} ${
                  totalImagenes === 1 ? 'imagen' : 'imágenes'
                }. Tocá la X de cualquier card para descartarla.`}
          </p>
          {/* Fondo + Modelo globales: aplican a TODA la tanda. El botón de
              Modelo solo aparece si hay alguna pose con-modelo (las vistas
              solo-producto no usan modelo). */}
          {/* Mobile: filas compactas (label izq · valor der · chevron). */}
          {activeCards.length > 0 && hasAjustesGlobales && (
            <div className="sm:hidden flex flex-col gap-2.5 mt-4">
              {hasFondo && (
              <button
                type="button"
                onClick={onOpenFondo}
                className="w-full flex items-center justify-between gap-3 px-4 py-3.5 rounded-xl border border-neutral-200 bg-white active:scale-[0.98] transition-all"
              >
                <span className="inline-flex items-center gap-2 text-[14px] text-brand-black shrink-0">
                  <ImageIcon className="w-[18px] h-[18px]" />
                  Fondo
                </span>
                <span className="inline-flex items-center gap-1.5 min-w-0">
                  <span className="text-[14px] font-semibold text-brand-black truncate">
                    {fondoLabel}
                  </span>
                  <ChevronRight className="w-4 h-4 text-[#3C3C43]/40 shrink-0" />
                </span>
              </button>
              )}
              {hayInfografia && (
                <button
                  type="button"
                  onClick={() => setInfografiaSheetOpen(true)}
                  className="w-full flex items-center justify-between gap-3 px-4 py-3.5 rounded-xl border border-neutral-200 bg-white active:scale-[0.98] transition-all"
                >
                  <span className="inline-flex items-center gap-2 text-[14px] text-brand-black shrink-0">
                    <Ruler className="w-[18px] h-[18px]" />
                    Infografía
                  </span>
                  <span className="inline-flex items-center gap-1.5 min-w-0">
                    <span className="text-[14px] font-semibold text-brand-black truncate">
                      {resumenInfografia}
                    </span>
                    <ChevronRight className="w-4 h-4 text-[#3C3C43]/40 shrink-0" />
                  </span>
                </button>
              )}
              {hasModelo && (
                <button
                  type="button"
                  onClick={onOpenModel}
                  className="w-full flex items-center justify-between gap-3 px-4 py-3.5 rounded-xl border border-neutral-200 bg-white active:scale-[0.98] transition-all"
                >
                  <span className="inline-flex items-center gap-2 text-[14px] text-brand-black shrink-0">
                    <UserRound className="w-[18px] h-[18px]" />
                    Modelo
                  </span>
                  <span className="inline-flex items-center gap-1.5 min-w-0">
                    <span className="text-[14px] font-semibold text-brand-black truncate">
                      {modeloLabel}
                    </span>
                    <ChevronRight className="w-4 h-4 text-[#3C3C43]/40 shrink-0" />
                  </span>
                </button>
              )}
            </div>
          )}

          {/* Desktop: cards del mismo ancho que las de vista (grid igual),
              horizontales (thumbnail cuadrado a la izquierda + label). El
              caso "automático" se anima igual que el tile del picker. */}
          {activeCards.length > 0 && hasAjustesGlobales && (
            <div className="hidden sm:grid grid-cols-2 lg:grid-cols-3 gap-3 mt-4">
              {hayInfografia && (
                <button
                  type="button"
                  onClick={() => setInfografiaSheetOpen(true)}
                  className="flex items-center gap-3 p-3 rounded-2xl border border-neutral-200 bg-white text-left hover:border-brand-primary/40 hover:shadow-sm active:scale-[0.99] transition-all"
                >
                  <div className="w-24 h-24 shrink-0 rounded-xl overflow-hidden relative bg-neutral-100 flex items-center justify-center">
                    <Ruler className="w-7 h-7 text-[#3C3C43]/40" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10.5px] uppercase tracking-wide font-medium text-[#3C3C43]/55">
                      Infografía
                    </p>
                    <p className="text-[14px] font-semibold text-brand-black truncate mt-0.5">
                      {resumenInfografia}
                    </p>
                  </div>
                </button>
              )}
              {hasFondo && (
              <button
                type="button"
                onClick={onOpenFondo}
                className="flex items-center gap-3 p-3 rounded-2xl border border-neutral-200 bg-white text-left hover:border-brand-primary/40 hover:shadow-sm active:scale-[0.99] transition-all"
              >
                <div className="w-24 h-24 shrink-0 rounded-xl overflow-hidden relative bg-neutral-100">
                  {fondoIsAuto ? (
                    <div className="absolute inset-0 bg-brand-primary flex items-center justify-center">
                      <AnimatedBlurBg />
                      <ImageIcon className="relative z-10 w-7 h-7 text-white drop-shadow" />
                    </div>
                  ) : fondoImg ? (
                    <CachedImg
                      src={fondoImg}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  ) : fondoIsBlanco ? (
                    <div className="absolute inset-0 bg-white flex items-center justify-center">
                      <div className="w-10 h-10 rounded-md border-2 border-neutral-300 bg-white" />
                    </div>
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center text-[#3C3C43]/40">
                      <ImageIcon className="w-7 h-7" />
                    </div>
                  )}
                </div>
                <div className="min-w-0">
                  <p className="text-[10.5px] uppercase tracking-wide font-medium text-[#3C3C43]/55">
                    Fondo
                  </p>
                  <p className="text-[14px] font-semibold text-brand-black truncate mt-0.5">
                    {fondoLabel}
                  </p>
                </div>
              </button>
              )}
              {hasModelo && (
                <button
                  type="button"
                  onClick={onOpenModel}
                  className="flex items-center gap-3 p-3 rounded-2xl border border-neutral-200 bg-white text-left hover:border-brand-primary/40 hover:shadow-sm active:scale-[0.99] transition-all"
                >
                  <div className="w-24 h-24 shrink-0 rounded-xl overflow-hidden relative bg-neutral-100">
                    {modeloIsAuto ? (
                      <div className="absolute inset-0 bg-brand-primary flex items-center justify-center">
                        <AnimatedBlurBg />
                        <UserRound className="relative z-10 w-7 h-7 text-white drop-shadow" />
                      </div>
                    ) : modeloImg ? (
                      <CachedImg
                        src={modeloImg}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center text-[#3C3C43]/40">
                        <UserRound className="w-7 h-7" />
                      </div>
                    )}
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10.5px] uppercase tracking-wide font-medium text-[#3C3C43]/55">
                      Modelo
                    </p>
                    <p className="text-[14px] font-semibold text-brand-black truncate mt-0.5">
                      {modeloLabel}
                    </p>
                  </div>
                </button>
              )}
            </div>
          )}

          {/* Los ajustes de fondo blanco (tono + luz) NO van acá: viven dentro
              del picker de fondos, pegados a la opción "Fondo blanco" de la que
              dependen. Tenerlos también acá era el mismo estado en dos controles
              de la misma pantalla. */}
        </div>

        {/* Grid de cards. Las cards mantienen su diseño de siempre — lo que
            agregamos es la clasificación por producto: cada grupo abre con
            un header (col-span-full) y debajo van sus cards. */}
        <div className="px-5 sm:px-8 py-5 sm:pb-20">
          {activeCards.length === 0 ? null : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {medidasError && (
                <div className="col-span-full mb-2 px-3.5 py-2.5 rounded-xl bg-error-soft border border-error/30 text-[13px] text-error-text">
                  {medidasError}
                </div>
              )}
              {groupedByProduct.map(({ product, cards }) =>
                // Modo Infografía separado: una tira horizontal por producto en
                // vez del header + bloques + card. Ver ADR-814.
                soloInfografia ? (
                  <React.Fragment key={product.id}>
                    {renderInfografiaProductStrip(product)}
                  </React.Fragment>
                ) : (
                <React.Fragment key={product.id}>
                  {renderProductHeader(product)}
                  {/* Medidas: se ocultan si la categoría no se vende por tamaño
                      (herramienta/decoración). Categoría desconocida/null → se muestran. */}
                  {modoMuebles &&
                    categoriaAdmiteMedidas(categoriaPorVariante[product.id]) && (
                    <MedidasProductoBlock
                      anchorId={`medidas-${product.id}`}
                      medidas={medidasPorVariante[product.id] ?? null}
                      onChange={(next) =>
                        handleMedidasChange(product.id, next)
                      }
                      // `&& !medidasCargando`: sin esto, mientras el fetch
                      // vuelve las medidas son null y TODOS los productos
                      // parpadean en rojo un instante. Por-producto: respeta el
                      // override de "mostrar medidas" de cada uno (ADR-808).
                      exigidas={
                        productoExigeMedidas(product.id) && !medidasCargando
                      }
                    />
                  )}
                  {/* Atajo de características: mismo patrón que el de medidas, pero
                      para TODAS las categorías (una herramienta también tiene
                      atributos). Muestra lo que dedujo la IA y deja editarlo en el
                      momento; persiste por variante. Ver ADR-813. */}
                  {modoMuebles && (
                    <AtributosProductoBlock
                      anchorId={`atributos-${product.id}`}
                      atributos={atributosPorProducto[product.id] ?? []}
                      onChange={(next) =>
                        handleAtributosChange(product.id, next)
                      }
                    />
                  )}
                  {cards.map(renderCard)}
                  {renderAddPoseCard(product)}
                </React.Fragment>
                ),
              )}
            </div>
          )}
        </div>
      </div>

      {/* Mobile footer: botón Generar full-width. */}
      {isMobile && (
        <div
          className="shrink-0 border-t border-black/[0.06] bg-white px-5 pt-3 pb-4"
          style={{
            paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)',
          }}
        >
          {/* Sin cards: hint que emerge por ARRIBA del botón + click que
              scrollea+sacude el empty-state (patrón influencer, ADR-555). */}
          <div className="relative">
            <div
              aria-hidden
              className={`absolute inset-0 rounded-2xl bg-white/85 backdrop-blur-md shadow-md ring-1 ring-black/[0.04] transition-opacity duration-300 ${
                activeCards.length === 0 ? 'opacity-100' : 'opacity-0'
              }`}
            />
            <div
              className={`relative z-10 overflow-hidden transition-all duration-300 ease-out ${
                activeCards.length === 0
                  ? 'max-h-16 opacity-100'
                  : 'max-h-0 opacity-0'
              }`}
            >
              <p className="pt-2 pb-1.5 px-4 text-center text-[12.5px] leading-snug text-[#3C3C43]/70">
                Agregá al menos una vista o producto para generar.
              </p>
            </div>
            <div className="relative z-10">
              <button
                type="button"
                onClick={() => {
                  if (activeCards.length === 0) {
                    scrollToAndShake('catalogo-cards-zone');
                    return;
                  }
                  handleGenerate();
                }}
                aria-disabled={activeCards.length === 0}
                className={`w-full inline-flex items-center justify-center gap-2 py-3.5 px-5 rounded-2xl bg-brand-primary hover:bg-brand-primary-hover text-white text-[15.5px] font-semibold transition-colors ${
                  activeCards.length === 0 ? 'opacity-60' : ''
                }`}
              >
                <Sparkles className="w-5 h-5" />
                <span>Generar</span>
                {activeCards.length > 0 && (
                  <span className="inline-flex items-center gap-1 bg-white text-brand-black px-2 py-0.5 rounded-full text-[12.5px] font-semibold">
                    <img
                      src="/images/token_icon.png"
                      alt=""
                      aria-hidden="true"
                      className="w-3.5 h-3.5 object-contain"
                    />
                    {totalImagenes}
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sheet lateral para combinar productos. Abre DIRECTO al grid de
          productos cargados (sin subir/elegir), con la selección actual
          tildada y el principal marcado. Sirve para una card o para el
          outfit de todo un producto. */}
      <MultiSlotProductPickerSheet
        open={sheetTarget !== null}
        onClose={() => setSheetTarget(null)}
        currentSelection={sheetSelection}
        onConfirm={handleSheetConfirm}
        userCompanyId={userCompanyId}
        brandId={brandId}
        rubro={rubro}
        userPlan={userPlan}
        tiposProductoSugeridos={tiposProductoSugeridos}
        onProductsRefreshed={onProductsRefreshed}
        directToPick
        initialSelection={sheetSelection}
        showPrimary
        title={
          sheetTarget?.type === 'product'
            ? 'Editar outfit'
            : 'Combinar productos'
        }
        pickDescription="Armá el outfit con el que se va a mostrar el producto principal: sumá las prendas o accesorios que querés que lleve en la misma imagen."
      />

      {/* "Agregar pose" — picker liviano que abre el "+" de un grupo. Suma
          la(s) pose(s) elegida(s) a ESE producto (hereda su outfit). */}
      <InfografiaConfigSheet
        open={infografiaSheetOpen}
        config={infografiaConfig}
        onChange={setInfografiaConfig}
        onClose={() => setInfografiaSheetOpen(false)}
        sinMedidasCargadas={
          modoMuebles &&
          selectedProducts.every((p) => !tieneMedidas(medidasPorVariante[p.id]))
        }
        // Global: la sección de medidas aparece si AL MENOS UN producto de la
        // tanda es de una categoría que se vende por tamaño. Si vienen todas
        // herramientas/decoración/iluminación, se oculta (#4).
        admiteMedidas={selectedProducts.some((p) =>
          categoriaAdmiteMedidas(categoriaPorVariante[p.id]),
        )}
      />

      {/* Sheet de infografía POR PRODUCTO (override parcial). Mismo componente
          que el global, en variante 'producto'. Ver ADR-808. */}
      <InfografiaConfigSheet
        open={!!infografiaProductoOpen}
        variante="producto"
        productoNombre={
          groupedByProduct.find(
            (g) => g.product.id === infografiaProductoOpen,
          )?.product.name
        }
        config={
          infografiaProductoOpen
            ? configInfografiaDe(infografiaProductoOpen)
            : infografiaConfig
        }
        personalizado={
          !!infografiaProductoOpen &&
          esProductoPersonalizado(infografiaProductoOpen)
        }
        onTogglePersonalizado={(v) =>
          infografiaProductoOpen &&
          togglePersonalizarProducto(infografiaProductoOpen, v)
        }
        onChange={(next) =>
          infografiaProductoOpen &&
          handleProductoConfigChange(infografiaProductoOpen, next)
        }
        onClose={() => setInfografiaProductoOpen(null)}
        sinMedidasCargadas={
          !!infografiaProductoOpen &&
          !tieneMedidas(medidasPorVariante[infografiaProductoOpen])
        }
        // Por producto: si su categoría no se vende por tamaño (herramienta,
        // decoración, iluminación) se oculta la sección de medidas (#3). Sin
        // producto abierto default true (no aplica).
        admiteMedidas={
          !infografiaProductoOpen ||
          categoriaAdmiteMedidas(categoriaPorVariante[infografiaProductoOpen])
        }
        atributos={
          infografiaProductoOpen
            ? (atributosPorProducto[infografiaProductoOpen] ?? [])
            : []
        }
        onAtributosChange={(next) =>
          infografiaProductoOpen &&
          handleAtributosChange(infografiaProductoOpen, next)
        }
      />

      <AgregarPoseSheet
        etiqueta={modoMuebles ? 'vista' : 'pose'}
        open={!!addPoseProduct}
        productName={addPoseProduct?.name ?? ''}
        productModelView={addPoseProduct?.modelView ?? null}
        poses={addablePoses}
        onConfirm={(selectedPoses) => {
          if (addPoseProduct) {
            selectedPoses.forEach((v) => addPoseToProduct(v, addPoseProduct.id));
          }
          setAddPoseProductId(null);
        }}
        onClose={() => setAddPoseProductId(null)}
      />
    </div>
  );
};
